import axios from 'axios';
import { useRef } from 'react';
import { toast } from 'react-toastify';
import { ToastError } from 'src/components/atoms/toast/toast-error';
import { useAppDispatch } from 'src/reducers';
import { logout, resetAuthState } from 'src/reducers/authentication.reducer';
import { getJwtToken } from 'src/utils/auth-utils';

const TIMEOUT = 1 * 60 * 1000;
axios.defaults.timeout = TIMEOUT;

export const useAxiosInterceptor = () => {
  const init = useRef<boolean>(false);
  const dispatch = useAppDispatch();

  if (!init.current) {
    init.current = true;
    const pendingRequests = new Map();

    axios.interceptors.request.use(
      async request => {
        // setup cancel duplicate requests
        const requestIdentifier = `${request.url}_${request.method}`;

        const token = getJwtToken();
        if (token) {
          request.headers!.Authorization = `Bearer ${token}`;
        }

        return request;
      },
      error => Promise.reject(error)
    );

    axios.interceptors.response.use(
      response => {
        // cleanup cancel duplicate requests
        const requestIdentifier = `${response.config.url}_${response.config.method}`;
        pendingRequests.delete(requestIdentifier);

        return response;
      },
      async error => {
        // cleanup cancel duplicate requests
        if (error.config) {
          const requestIdentifier = `${error.config.url}_${error.config.method}`;
          pendingRequests.delete(requestIdentifier);
        }

        const status = error.response?.status;

        if (status === 500 && typeof error.response?.data === 'string' && error.response?.data.includes('Proxy error')) {
          toast(() => ToastError('Errore di connessione al server. Verifica che il server sia online.'));
          return Promise.reject(error);
        }

        if (status === 400) {
          const errorMessage = exceptionTranslationMap(error.response?.data?.message) || 'Errore nella gestione della richiesta';
          toast(() => ToastError(errorMessage));
        }

        if (status === 500) {
          const errorMessage = exceptionTranslationMap(error.response?.data?.message) || 'Errore interno al server';
          toast(() => ToastError(errorMessage));
        }

        if (status === 401) {
          dispatch(logout());
          dispatch(resetAuthState());
        }
        return Promise.reject(error);
      }
    );
  }
};

export const exceptionTranslationMap = (exception: string) => {
  const exceptionMap: Record<string, string> = {
    INVALID_CREDENTIALS: 'Credenziali non valide',
    USER_NOT_ACTIVE: 'Utente non attivo',
    USER_NOT_FOUND: 'Utente non trovato',
    USER_ALREADY_EXISTS: 'Utente già esistente',
    INVALID_TOKEN: 'Token non valido',
    INSUFFICIENT_PERMISSIONS: 'Permessi insufficienti',
    ROLE_HAS_ASSOCIATED_CAPABILITIES: 'Il ruolo ha autorizzazioni associate',
  };

  return exceptionMap[exception] || exception;
};
